/**
 * The 30-day outcome counts, exactly as the server counted them (A4).
 *
 * FIVE NUMBERS, RENDERED AND NOT RECOMPUTED. `stats_30d` arrives aggregated by
 * PostgreSQL over a window the backend resolved on the Swiss calendar. Counting
 * the run rows in the browser instead would tally one PAGE of the history and
 * silently disagree with the server the moment somebody turned to page two.
 *
 * AN EMPTY WINDOW IS ZEROS, NOT "Keine Daten". "How many runs succeeded"
 * has a correct answer when nothing ran, and it is zero — the cards are not
 * broken, the month was quiet. A sentence underneath says so in words, because
 * four zeros alone could also be read as a failure to load.
 *
 * NO RATE, NO TREND, NO DONUT. A3 publishes no percentage, and one computed
 * here would be a second statistic with no server-side twin — the exact thing
 * `summarize_sync_runs` refuses for its own success rate. The outcome counts
 * also need not add up to `total`: a `running` row is in the total and in none
 * of them, which is correct and would look like an error in a pie chart.
 *
 * `indeterminate` HAS ITS OWN CARD SINCE D11b (E27). The server has counted it
 * apart since D6a; it is neither a success nor a failure, so it is neither of
 * those cards — "Unklar", in amber. Every number here counts EXECUTIONS, never
 * people: for the move-in address one tenant can be checked on several days,
 * and the heading says "Produktionsläufe" so nobody reads "3" as three tenants.
 *
 * TEST RUNS ARE ALREADY EXCLUDED, server-side. Nothing here filters, and the
 * caption says "Produktionsläufe" so the number is not read as everything that
 * happened.
 */
import React from "react";
import { Info } from "lucide-react";

import { formatDateOnly } from "../../../utils/datetime";
import { AutomationIconTile, automationStatIcon } from "./automationIcons";

/**
 * Tone per card, and green means exactly one thing on this page.
 *
 * `succeeded` is positive and `failed` is bad, which is the same pairing the
 * run history's badges use. `skipped` stays quiet rather than warning — a
 * skipped run is usually the correct outcome (nobody had a birthday today) —
 * and `total` is a tally, not an outcome, so it takes the neutral brand tone
 * rather than colouring a number that is only ever "how many".
 */
export const AUTOMATION_STAT_CARDS = [
  { key: "succeeded", label: "Erfolgreich", tone: "positive" },
  { key: "failed", label: "Fehlgeschlagen", tone: "bad" },
  { key: "skipped", label: "Übersprungen", tone: "muted" },
  { key: "indeterminate", label: "Unklar", tone: "warning" },
  { key: "total", label: "Gesamt", tone: "neutral" },
];

export default function AutomationStatsCards({ stats }) {
  if (!stats) return null;
  const isEmpty = Number(stats.total) === 0;

  return (
    <section aria-labelledby="automation-stats-heading" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3
          id="automation-stats-heading"
          className="m-0 text-[12.5px] font-semibold text-foreground"
        >
          Produktionsläufe der letzten 30 Tage
        </h3>
        <p className="m-0 text-[11.5px] text-muted-foreground">
          {formatDateOnly(stats.window_from)} – {formatDateOnly(stats.window_to)}
        </p>
      </div>

      {/* TWO COLUMNS, NOT FOUR. The detail panel is ~410px wide by design, and
          `sm:grid-cols-4` switches on the VIEWPORT rather than on this
          container — so on a 1440px screen four cards shared 410px and clipped
          "Fehlgeschlagen" to "FEHLGESCHLAG…". A count whose label is cut in
          half is worse than one that takes a second row. */}
      <dl className="m-0 grid grid-cols-2 gap-2">
        {AUTOMATION_STAT_CARDS.map((card) => (
          <div
            key={card.key}
            // Five cards in two columns: the tally takes the whole last row
            // rather than leaving a hole beside it.
            className={`flex items-center gap-2.5 rounded-[10px] border border-border bg-card px-3 py-2.5 ${
              card.key === "total" ? "col-span-2" : ""
            }`}
          >
            <AutomationIconTile
              icon={automationStatIcon(card.key)}
              tone={card.tone}
            />
            <div className="flex min-w-0 flex-col">
              {/* No `tracking-wide` and a hair smaller than the other labels
                  on this page: "FEHLGESCHLAGEN" is fourteen characters of one
                  unbreakable word, and in a 170px card beside a 40px tile the
                  extra letter-spacing was the difference between fitting and
                  splitting into "FEHLGESCHLAGE / N". */}
              <dt className="break-words text-[10px] font-semibold uppercase text-muted-foreground">
                {card.label}
              </dt>
              <dd className="m-0 text-[19px] font-semibold leading-tight text-foreground">
                {Number(stats[card.key]) || 0}
              </dd>
            </div>
          </div>
        ))}
      </dl>

      {isEmpty ? (
        <p className="m-0 flex items-center gap-2 rounded-[10px] border border-border bg-muted/60 px-3 py-2 text-[11.5px] text-muted-foreground">
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Noch keine Ausführungen in diesem Zeitraum.
        </p>
      ) : null}
    </section>
  );
}
